// src/components/BillingGraceBanner.tsx
// Shows a countdown banner when billing.status === 'grace_period'
// Place this inside Layout.tsx just below the top AppBar

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Stack, LinearProgress, Collapse,
} from '@mui/material';
import TimerIcon       from '@mui/icons-material/Timer';
import PaymentsIcon    from '@mui/icons-material/Payments';
import CloseIcon       from '@mui/icons-material/Close';
import { useAuth }     from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// ── Countdown helper ──────────────────────────────────────────────────────────
function useCountdown(endsAt: string | null) {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    if (!endsAt) return;

    const update = () => {
      const diff = new Date(endsAt).getTime() - Date.now();
      setRemaining(Math.max(0, diff));
    };

    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [endsAt]);

  const totalMs   = 3 * 24 * 60 * 60 * 1000; // 3 days
  const pct       = remaining > 0 ? Math.round((remaining / totalMs) * 100) : 0;
  const days      = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const hours     = Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes   = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
  const seconds   = Math.floor((remaining % (1000 * 60)) / 1000);
  const expired   = remaining === 0;

  return { days, hours, minutes, seconds, pct, expired, remaining };
}

// ── Digit block ───────────────────────────────────────────────────────────────
const Digit: React.FC<{ value: number; label: string; urgent: boolean }> = ({ value, label, urgent }) => (
  <Box sx={{ textAlign: 'center', minWidth: 40 }}>
    <Box sx={{
      px: 1,
      py: 0.25,
      borderRadius: 1,
      background: urgent ? 'rgba(248,113,113,0.2)' : 'rgba(251,191,36,0.15)',
      border: `1px solid ${urgent ? 'rgba(248,113,113,0.4)' : 'rgba(251,191,36,0.3)'}`,
      mb: 0.25,
    }}>
      <Typography sx={{
        fontFamily: '"DM Mono", "Fira Code", monospace',
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: 1.2,
        color: urgent ? '#f87171' : '#fbbf24',
        letterSpacing: '0.05em',
      }}>
        {String(value).padStart(2, '0')}
      </Typography>
    </Box>
    <Typography sx={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
      {label}
    </Typography>
  </Box>
);

// ── Separator ─────────────────────────────────────────────────────────────────
const Sep: React.FC<{ urgent: boolean }> = ({ urgent }) => (
  <Typography sx={{ color: urgent ? '#f87171' : '#fbbf24', fontWeight: 700, fontSize: '1rem', mb: 1.5, opacity: 0.7 }}>:</Typography>
);

// ── Main component ────────────────────────────────────────────────────────────
const BillingGraceBanner: React.FC = () => {
  const { billingStatus, user } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);

  console.log('BANNER DEBUG:', JSON.stringify({ billingStatus, role: user?.role, dismissed }));

  console.log('🧾 BillingGraceBanner:', { 
    billingStatus, 
    userRole: user?.role,
    dismissed 
  });

  const { days, hours, minutes, seconds, pct, expired } =
    useCountdown(billingStatus?.gracePeriodEndsAt ?? null);

  // Only show for company users in grace period
  if (!user || !['company_admin', 'fleet_manager', 'supervisor'].includes(user.role)) return null;
  if (billingStatus?.status !== 'grace_period') return null;
  if (dismissed) return null;

  const urgent = days === 0; // less than 1 day left
  const barColor = urgent ? '#f87171' : days < 2 ? '#fb923c' : '#fbbf24';

  return (
    <Collapse in={true}>
      <Box sx={{
        background: urgent
          ? 'linear-gradient(135deg, rgba(127,29,29,0.95) 0%, rgba(69,10,10,0.98) 100%)'
          : 'linear-gradient(135deg, rgba(78,52,6,0.95) 0%, rgba(54,28,4,0.98) 100%)',
        borderBottom: `1px solid ${urgent ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.25)'}`,
        px: { xs: 2, sm: 3 },
        py: 1,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Subtle background texture */}
        <Box sx={{
          position: 'absolute', inset: 0, opacity: 0.03,
          backgroundImage: 'repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)',
          backgroundSize: '8px 8px',
          pointerEvents: 'none',
        }} />

        {/* Progress bar */}
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            position: 'absolute', top: 0, left: 0, right: 0, height: 2,
            bgcolor: 'rgba(255,255,255,0.05)',
            '& .MuiLinearProgress-bar': {
              bgcolor: barColor,
              transition: 'transform 1s linear',
            },
          }}
        />

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, md: 2 }}
        >
          {/* Left: icon + message */}
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{
              width: 32, height: 32, borderRadius: 1.5,
              background: urgent ? 'rgba(248,113,113,0.15)' : 'rgba(251,191,36,0.12)',
              border: `1px solid ${urgent ? 'rgba(248,113,113,0.3)' : 'rgba(251,191,36,0.25)'}`,
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}>
              <TimerIcon sx={{ fontSize: 16, color: urgent ? '#f87171' : '#fbbf24' }} />
            </Box>
            <Box>
              <Typography sx={{
                fontWeight: 800,
                fontSize: '0.82rem',
                color: urgent ? '#fca5a5' : '#fde68a',
                lineHeight: 1.2,
              }}>
                {urgent ? '⚠️ Account locks very soon' : 'Payment overdue — grace period active'}
              </Typography>
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.45)', mt: 0.25 }}>
                {expired
                  ? 'Grace period has expired. Your account may be locked.'
                  : 'Pay your outstanding invoice to avoid losing access.'
                }
              </Typography>
            </Box>
          </Stack>

          {/* Center: countdown */}
          {!expired && (
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
              <Digit value={days}    label="days"  urgent={urgent} />
              <Sep urgent={urgent} />
              <Digit value={hours}   label="hrs"   urgent={urgent} />
              <Sep urgent={urgent} />
              <Digit value={minutes} label="min"   urgent={urgent} />
              <Sep urgent={urgent} />
              <Digit value={seconds} label="sec"   urgent={urgent} />
            </Stack>
          )}

          {/* Right: CTA + dismiss */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<PaymentsIcon sx={{ fontSize: 14 }} />}
              onClick={() => navigate('/invoices')}
              sx={{
                borderRadius: 1.5,
                textTransform: 'none',
                fontWeight: 800,
                fontSize: '0.75rem',
                px: 1.5,
                py: 0.5,
                background: urgent ? '#f87171' : '#fbbf24',
                color: urgent ? '#fff' : '#000',
                boxShadow: 'none',
                '&:hover': {
                  background: urgent ? '#ef4444' : '#f59e0b',
                  boxShadow: 'none',
                },
              }}
            >
              Pay Now
            </Button>
            <Box
              component="button"
              aria-label="Dismiss payment reminder"
              onClick={() => setDismissed(true)}
              sx={{
                background: 'none', border: 'none', cursor: 'pointer',
                p: 0.5, minWidth: 44, minHeight: 44, borderRadius: 1,
                color: 'rgba(255,255,255,0.3)',
                display: 'flex', alignItems: 'center',
                '&:hover': { color: 'rgba(255,255,255,0.6)', background: 'rgba(255,255,255,0.05)' },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </Box>
          </Stack>
        </Stack>
      </Box>
    </Collapse>
  );
};

export default BillingGraceBanner;